// RoadEazy Domain Configuration
export const roadEazyConfig = {
  // Basic domain information
  id: 'road-eazy',
  name: 'RoadEazy',
  tagline: 'Advanced Camera Monitoring',
  description: 'A single platform and AI-powered dash camera combines telematics, video safety, maintenance and more. To improve your operations, safety and productivity.',
  
  // Assets
  logo: '/assets/logos/road-eazy-logo',
  whiteLogo: '/assets/logos/road-eazy-white-logo',
  favicon: '/assets/favicons/road-eazy-favicon.png',
  icon: '/assets/icons/road-eazy',
  
  // Navigation
  pages: ['home', 'solutions', 'resources', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Resources', path: '/resources' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
              
  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/roadeazy',

  // UI Configuration
  variant: 'dark',
  showFeatures: false,
  showTestimonials: false,
  
  // Content sections
  hero: {
    title: 'RoadEazy Advanced Dash Cam Solution',
    subtitle: 'A single platform and AI-powered dash camera combines telematics, video safety, maintenance and more. To improve your operations, safety and productivity.',
    features: ['Simple Setup', 'Real-time Monitoring', 'Mobile Access', 'AI Powered']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'RoadEazy Gallery',
    subtitle: 'Explore our advanced AI-powered camera monitoring and fleet management solution',
    description: ''
  },

  features: {
    title: 'Advanced RoadEazy Dash Cam Features',
    subtitle: 'AI-Driven, Driver Centered, Fleet- Focused',
    description: 'Built for fleets that put lives first with proactive monitoring, real-time alerts, and driver-first design.',
    items: [
      {
        title: 'Certified 4G LTE Connectivity',
        description: '4G connectivity certified by PTCRB, AT&T and T-Mobile',
        icon: '📱'
      },
      {
        title: 'Advanced GPS Tracking',
        description: 'GPS and A-GPS for fast and precise vehicle location',
        icon: '🌍'
      },
      {
        title: 'Dual Channel HD',
        description: 'Captures road and cabin clearly, day and night',
        icon: '🎥'
      },
      {
        title: 'Robust Vehicle Diagnostics',
        description: 'OBDII and J1939 compatibility and manufacturer codes support',
        icon: '🚗'
      },
      {
        title: 'Real Time Edge AI',
        description: 'Instantly detects unsafe driving behavior with a powerful processor',
        icon: '🤖'
      },
      {
        title: 'Built-in Storage',
        description: 'Securely captures video in the harshest conditions',
        icon: '📁'
      }
    ]
  },
  
  solutions: {
    title: 'RoadEazy All-In-One Platform',
    subtitle: 'Fleet Intelligence Solution',
    description: 'Too many systems, too little clarity. Roadeazy brings telematics, video, and maintenance together — one platform, one device, total control.',
    sections: [
      {
        title: 'Comprehensive Vehicle Data and Diagnostics',
        description: 'Gain unmatched insights into fleet performance with RZ1’s advanced telematics. Access real-time diagnostics, vehicle metrics, and precise GPS tracking to proactively manage maintenance and reduce downtime.\n Supports a wide range of vehicle protocols including OBD-II and J1939. \nBuilt-in GPS with optional external antenna.',
      },
      {
        title: 'Intelligent Dual-View Video',
        description: 'The RZ1 captures clear road and cabin footage, with integrated edge AI instantly identifying risks like distracted driving, phone usage, tailgating, and harsh behaviors—improving fleet safety and accountability.'
      },
      {
        title: 'Reliable, Certified Connectivity',
        description: 'The RZ1 ensures secure, uninterrupted fleet data transmission, certified by PTCRB, AT&T, and T-Mobile, keeping managers reliably informed even under challenging conditions.',
      },
      {
        title: 'GPS, Odometer, Fuel — All in One View',
        description: 'Track every vehicle live, read engine data, and get instant alerts. Roadeazy connects to OBD-II and J1939, delivering fuel levels, fault codes, trip logs, and more — so you\'re always in control.'
      },
      {
        title: 'AI That Sees, Learns, and Protects',
        description: 'Detect distraction, fatigue, and risky driving in real time. Roadeazy\'s AI-powered cameras and alerts help prevent incidents before they happen — keeping your drivers safe and your business protected.'
      },
      {
        title: 'Preventive Maintenance, Automated',
        description: 'Stop reacting to breakdowns. Roadeazy monitors DTCs, and battery voltage in real time, allows you to create and track your maintanence schedule — so you can plan repairs, reduce downtime, and keep your fleet running smooth.'
      }
    ]
  },
  
  
  testimonials: [
    {
      content: "RoadEazy has streamlined our road monitoring operations and reduced support calls by 50%.",
      author: "Steven Brown",
      role: "Fleet Coordinator",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/48.jpg"
    },
    {
      content: "The RZ1 Dash Cam by RoadEazy have significantly improved our fleet's safety and compliance.",
      author: "Michelle Davis",
      role: "Safety Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/36.jpg"
    }
  ],

  blog: {
    title: 'RoadEazy Blog',
    subtitle: 'Latest News and Insights',
    description: 'Stay informed with the latest news and insights in fleet management and industry best practices.',
  },

  resourceCenter: {
    title: 'RoadEazy Resource Center',
    subtitle: 'Expert guides and educational resources',
    description: 'Everything you need to know about RoadEazy camera systems and safety solutions.',
    showComparePrices: false,

    hero: {
      title: 'RoadEazy Resource Center',
      subtitle: 'Your Complete Guide to RoadEazy Camera Systems',
      description: 'Comprehensive guides, expert insights, and frequently asked questions about RoadEazy camera systems and safety solutions.',
    },
    sections: [
      {
        title: 'What is the RoadEazy RZ1 Hub and Camera System?',
        description: 'RoadEazy RZ1 is an integrated video-telematics solution that combines AI-powered dual-camera monitoring, vehicle diagnostics, GPS tracking, and cellular connectivity—built to improve fleet safety, compliance, and efficiency.',
        items: []
      }
    ],
    benefitsSection: {
      title: 'Key Benefits',
      description: 'See how our solutions help your business'
    },
    benefits: [
      {
        title: 'Road Facing ADAS: Reducing Risk in the Open Road',
        description: 'Our road-facing Advanced Driver Assistance System (ADAS) improves driver behavior and alerts fleet managers. Alerting drivers instantly and notifies fleet managers for fast follow-up. Auto-Calibration allows for quick mount and drive. Warns drivers when following distance is unsafe, helping improve driver behavior over time. High-risk behavior triggers automatic alerts and video events for fleet review and coaching.',
      },
      {
        title: 'All Eyes on the Road',
        description: 'Our AI-powered in-cabin monitoring system acts as a second set of eyes—watching for signs of distraction, phone usage, and risky behaviors. Alerts at signs of cognitive distraction. Real-time recognition of handheld phone use with instant feedback.',
      },
      {
        title: 'From Data to Safer Decisions: Coaching That Drives Real Change',
        description: 'Roadeazy’s coaching engine turns insights into improvement. Coaching is supported by in-cabin prompts, performance trends, and configurable review workflows, making it easy to close the loop on safety.'
      },
      {
        title: 'Prevent Breakdowns. Reduce Costs. Extend Vehicle Life',
        description: 'Roadeazy Maintenance turns telematics data into action—track diagnostics, get alerts, and manage services from one dashboard.',
      }
    ],

    faqs: [
      {
        question: 'What are the key features of the RZ1 Hub?',
        answer: 'Key features include dual-camera coverage (road-facing and cabin), GNSS and cellular connectivity for real-time tracking and alerts, fast installation to reduce vehicle downtime, real-time safety and diagnostic alerts, geofence triggers, and cloud access with APIs for analytics and integrations.'
      },
      {
        question: 'How do I configure the RZ1 for each vehicle?',
        answer: 'You can configure each vehicle by editing vehicle settings such as name, license plate, tags, and fuel capacity, assigning drivers, and overriding fleet defaults with per-vehicle options including safety alert sensitivity, audio recording, camera profiles, external antenna use, SD card behavior, and standby timeout.'
      },
      {
        question: 'How long does installation take?',
        answer: 'Most RZ1 installations take under one hour per vehicle. The plug-and-play design and quick-connect harnesses help minimize vehicle downtime.'
      },
      {
        question: 'Can RoadEazy integrate with my existing fleet management software?',
        answer: 'Yes. RoadEazy offers API access and integration options for popular fleet management platforms, allowing GPS locations, video clips, and safety alerts to sync with existing systems.'
      },
      {
        question: 'How does RoadEazy protect driver privacy?',
        answer: 'RoadEazy protects driver privacy using encrypted data transmission, role-based access controls, and configurable recording settings. Cab-facing cameras can be set to record only during safety events or specific time windows.'
      },
      {
        question: 'How long is video footage stored?',
        answer: 'Video footage is stored on the device SD card and in the cloud. Cloud retention periods are configurable and typically range from 30 days up to one year, depending on plan and compliance requirements.'
      },
      {
        question: 'What types of safety alerts are included?',
        answer: 'Safety alerts include speeding, harsh braking, rapid acceleration, distracted driving, collision detection, and geofence entry or exit events. Notifications can be delivered via SMS, email, or in-platform alerts.'
      },
      {
        question: 'Can I access RoadEazy from my phone?',
        answer: 'Yes. The RoadEazy platform is mobile-friendly and supports live video, alert management, and trip history on smartphones and tablets.'
      }
    ],
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'RoadEazy™ | AI Dash Cam & Fleet Camera Systems',
    description: 'Professional fleet camera and dash cam solutions offering real-time video, AI driver monitoring, and GPS fleet tracking for safer, smarter fleet management.',
    keywords: [
      'ai dash cam',
      'fleet camera system',
      'commercial vehicle cameras',
      'truck dash cam',
      'fleet video monitoring',
      'driver safety camera',
      'gps fleet tracking',
      'fleet telematics cameras',
      'vehicle camera system'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'RoadEazy™ | AI Dash Cam & Fleet Camera Systems',
    ogDescription: 'Professional fleet camera and dash cam solutions offering real-time video, AI driver monitoring, and GPS fleet tracking for safer, smarter fleet management.',
    ogType: 'website',
    ogUrl: 'https://roadeazy.cam',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'RoadEazy™ | AI Dash Cam & Fleet Camera Systems',
    twitterDescription: 'AProfessional fleet camera and dash cam solutions offering real-time video, AI driver monitoring, and GPS fleet tracking for safer, smarter fleet management.',
    
    // Robots
    robots: 'index, follow',
    author: 'RoadEazy',
    publisher: 'RoadEazy',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'AI Dash Cam & Fleet Camera Systems | RoadEazy',
        description:
          'AI-powered dash camera platform combining telematics, video safety, and maintenance for improved fleet operations, safety, and productivity.',
        keywords: [
          'ai dash cam',
          'fleet camera system',
          'commercial dash cam',
          'truck camera system'
        ]
      },
  
      solutions: {
        title: 'Fleet Camera & Dash Cam Solutions | RoadEazy',
        description:
          'Professional fleet camera and dash cam solutions offering real-time video, AI driver monitoring, and GPS fleet tracking for safer, smarter fleet management.',
        keywords: [
          'fleet camera solutions',
          'dash cam for fleets',
          'driver monitoring system',
          'fleet video telematics'
        ]
      },
  
      products: {
        title: 'Commercial Dash Cams & Fleet Camera Products | RoadEazy',
        description:
          'Explore professional AI dash cams and multi-camera fleet systems designed for trucks, vans, and commercial vehicles to enhance safety and fleet efficiency.',
        keywords: [
          'commercial dash cam',
          'fleet camera hardware',
          'truck camera system'
        ]
      },
  
      blog: {
        title: 'RoadEazy Blog | Dash Cam & Camera System Insights',
        description:
          'Expert guides, news, and best practices on AI dash cams, fleet cameras, driver safety, and fleet technology for smarter, safer fleet management.',
        keywords: [
          'fleet safety blog',
          'dash cam guides',
          'fleet technology'
        ]
      },
      resources: {
        title: 'RoadEazy Resources | Installation Guides & Case Studies',
        description: 'Everything you need to know about RoadEazy camera systems, fleet safety solutions, AI monitoring, and advanced video technology for complete fleet protection.',
        keywords: ['RoadEazy resources', 'camera systems', 'safety solutions']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
